// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Maryland Bills Browser — browse all cached Maryland bills with client-side
 * filtering, sorting, and pagination via DataTable.
 */
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

import { useServices } from '@/app-shell/services'
import { useWorkspaceConfig } from '@/app-shell/hooks'
import { useAgents, useWorkspaceRenderers } from '@/subsystems/agents'
import type { NodeUserViewProps } from '@/core/types'

import {
  fetchMarylandBills,
  fetchBillFormatting,
  type MarylandBillMetadata,
} from '../services/maryland-bills.service'
import type { ProcessedAmendmentData } from '../models/types'
import { BillDetailsDialog } from '../components/dialogs/BillDetailsDialog'
import { BillAmendmentsDialog, processFormatting } from '../components/dialogs/BillAmendmentsDialog'
import type { DatasourceOption } from '../components/BillsFilters'

import { useBillsCache } from '../hooks/useBillsCache'
import { useAnalysisStatuses } from '@/applications/shared'
import { useRendererColumns } from '../hooks/useRendererColumns'
import { useMarylandBillsColumns } from '../hooks/useMarylandBillsColumns'
import { usePipelineJobTracker } from '../hooks/usePipelineJobTracker'
import { BillsBrowserFilters } from '../components/BillsBrowserFilters'
import { BillsCacheContent } from '../components/BillsCacheContent'

const PAGE_SIZE = 50

export const MarylandBillsBrowser: FC<NodeUserViewProps> = ({
  orgId,
  workspaceId,
  onLoadStart,
  onLoadEnd,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  // Datasources come from workspace config; agents (and their renderers and
  // run actions) are aggregated from every agent in the workspace.
  const { applicationSettings } = useWorkspaceConfig(orgId, workspaceId)
  const renderers = useWorkspaceRenderers(apiClient, orgId ?? '', workspaceId ?? '')
  // Every agent with a pipeline becomes a "Run <agent>" action in the row menu.
  const { data: agents } = useAgents(apiClient, orgId ?? '', workspaceId ?? '')
  const availableAgents = useMemo(
    () =>
      (agents ?? [])
        .filter((a) => Boolean(a.pipelineId))
        .map((a) => ({ agentId: a.agentId, label: a.label, pipelineId: a.pipelineId! })),
    [agents],
  )
  const datasources: DatasourceOption[] = useMemo(
    () => applicationSettings?.datasources ?? [],
    [applicationSettings?.datasources],
  )

  // Find the maryland datasource and extract sessions
  const marylandDs = useMemo(
    () => datasources.find((ds) => ds.id.toLowerCase().startsWith('maryland')),
    [datasources],
  )
  const sessionOptions = useMemo(() => {
    if (!marylandDs) return []
    return [...marylandDs.sessions].sort().reverse()
  }, [marylandDs])

  const [session, setSession] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Default to first (latest) session when options load
  useEffect(() => {
    if (sessionOptions.length > 0 && session === null) {
      setSession(sessionOptions[0])
    }
  }, [sessionOptions, session])

  // --- Shared hooks ---

  const fetchBillsFn = useCallback(
    () => fetchMarylandBills(apiClient, orgId, workspaceId, session!),
    [apiClient, orgId, workspaceId, session],
  )

  const cache = useBillsCache<MarylandBillMetadata>({
    apiClient,
    datasource: 'maryland',
    session,
    fetchBills: fetchBillsFn,
    onLoadStart, onLoadEnd,
  })

  // The datasets these columns display, each tagged with its owning agent — the
  // agent scopes the fetch, and a single-document refresh needs the keys too.
  const datasets = useMemo(
    () => Object.values(renderers ?? {}).flatMap(r =>
      r.slots.flatMap(slot => slot.type === 'dataset' && slot.datasetKey
        ? [{ agentId: r.agentId, datasetKey: slot.datasetKey }]
        : []),
    ),
    [renderers],
  )
  const agentIds = useMemo(
    () => Array.from(new Set(datasets.map(d => d.agentId))),
    [datasets],
  )

  const analysisStatuses = useAnalysisStatuses({
    apiClient, orgId, workspaceId,
    agentIds,
    groupId: session ?? undefined,
    enabled: session !== null,
  })

  // Dynamic columns from renderer definitions
  const getDocId = useCallback((bill: MarylandBillMetadata) => bill.BillNumber, [])
  const getBillNumber = useCallback((bill: MarylandBillMetadata) => bill.BillNumber, [])

  const rendererColumns = useRendererColumns<MarylandBillMetadata>({
    apiClient, orgId, workspaceId,
    groupId: session ?? undefined,
    renderers, analysisStatuses, getDocId, getBillNumber,
  })

  // --- Maryland-specific state ---

  const refreshDocumentStatus = useCallback((docId: string) => {
    analysisStatuses.refreshDocument(docId, datasets)
  }, [analysisStatuses, datasets])

  const jobTracker = usePipelineJobTracker({
    apiClient, orgId, workspaceId,
    onScoreJobComplete: refreshDocumentStatus,
    onReviewJobComplete: refreshDocumentStatus,
  })

  const handleRunAgent = useCallback((bill: MarylandBillMetadata, pipelineId: string) => {
    if (!session) return
    jobTracker.runPipeline({
      billKey: bill.BillNumber,
      billNumber: bill.BillNumber,
      sessionCode: session,
      pipelineId,
    })
  }, [session, jobTracker])

  // Detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedBill, setSelectedBill] = useState<MarylandBillMetadata | null>(null)
  const handleViewDetails = useCallback((bill: MarylandBillMetadata) => {
    setSelectedBill(bill)
    setDetailDialogOpen(true)
  }, [])

  // Amendments dialog state
  const [amendmentsDialogOpen, setAmendmentsDialogOpen] = useState(false)
  const [amendmentsBillNumber, setAmendmentsBillNumber] = useState('')
  const [amendmentData, setAmendmentData] = useState<ProcessedAmendmentData | null>(null)
  const [amendmentsLoading, setAmendmentsLoading] = useState(false)
  const [amendmentsError, setAmendmentsError] = useState<string | null>(null)

  const handleViewAmendments = useCallback((bill: MarylandBillMetadata) => {
    if (!session) return
    setAmendmentsBillNumber(bill.BillNumber)
    setAmendmentData(null)
    setAmendmentsError(null)
    setAmendmentsDialogOpen(true)
    setAmendmentsLoading(true)

    fetchBillFormatting(apiClient, orgId, workspaceId, 'maryland', session, bill.BillNumber)
      .then((formatting) => {
        if (formatting) {
          setAmendmentData(processFormatting(formatting, bill.BillNumber, session, 1))
        } else {
          setAmendmentData(null)
        }
      })
      .catch((err) => {
        setAmendmentsError(err instanceof Error ? err.message : 'Failed to load amendments')
      })
      .finally(() => setAmendmentsLoading(false))
  }, [apiClient, orgId, workspaceId, session])

  // Columns
  const baseColumns = useMarylandBillsColumns({
    session,
    onViewDetails: handleViewDetails,
    onViewAmendments: handleViewAmendments,
    agents: availableAgents,
    onRunAgent: handleRunAgent,
    reviewStatuses: jobTracker.statuses,
  })

  const columns = useMemo(() => {
    if (rendererColumns.columns.length === 0) return baseColumns
    const actions = baseColumns[baseColumns.length - 1]
    return [...baseColumns.slice(0, -1), ...rendererColumns.columns, actions]
  }, [baseColumns, rendererColumns.columns])

  // Filtering
  const filteredBills = useMemo(() => {
    let result = cache.bills
    if (search.trim()) {
      const term = search.toLowerCase()
      result = result.filter(
        (b) =>
          b.BillNumber.toLowerCase().includes(term) ||
          (b.Title?.toLowerCase().includes(term) ?? false) ||
          (b.Synopsis?.toLowerCase().includes(term) ?? false),
      )
    }
    return rendererColumns.applyFilters(result)
  }, [cache.bills, search, rendererColumns])

  return (
    <div className="space-y-4" data-testid="lr-maryland-browser">
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Maryland Bills Browser</CardTitle>
              <CardDescription>
                Browse all cached Maryland bills from mgaleg.maryland.gov
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {sessionOptions.length > 0 && (
                <Select value={session ?? ''} onValueChange={setSession}>
                  <SelectTrigger className="w-40" data-testid="lr-maryland-session-select">
                    <SelectValue placeholder="Session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionOptions.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <BillsBrowserFilters
            search={search}
            onSearchChange={setSearch}
            testIdPrefix="lr-maryland"
          />
        </CardHeader>
        <CardContent>
          <BillsCacheContent
            testIdPrefix="lr-maryland"
            cacheStatus={cache.cacheStatus}
            loading={cache.loading}
            error={cache.error}
            totalBills={cache.bills.length}
            filteredBills={filteredBills}
            columns={columns}
            pageSize={PAGE_SIZE}
            refreshingMessage="Building Maryland bills cache — this may take a moment..."
            loadingMessage="Loading Maryland bills..."
            statusExtra={analysisStatuses.loading ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading scores…
              </span>
            ) : undefined}
          />
        </CardContent>
      </Card>
      <BillDetailsDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        bill={selectedBill}
        testId="lr-maryland-details-dialog"
      />
      <BillAmendmentsDialog
        open={amendmentsDialogOpen}
        onOpenChange={setAmendmentsDialogOpen}
        amendmentData={amendmentData}
        billNumber={amendmentsBillNumber}
        loading={amendmentsLoading}
        error={amendmentsError}
        testId="lr-maryland-amendments-dialog"
      />
      {rendererColumns.dialog}
    </div>
  )
}
